import { defineStore } from 'pinia';
import { useDbStore } from '../dbStore';
import { useERezeptStore } from '../eRezeptStore';
import { useBasicResourcesStore } from './basicResources';

export const useMuster16ResourcesStore = defineStore('muster16Resources', {
  actions: {
    createCompositionResource(compositionData) {
      const compositionResource = {
        resourceType: 'Composition',
        id: compositionData.id,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Composition|1.1.0']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_FOR_Legal_basis',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_STATUSKENNZEICHEN',
              code: compositionData.legalBasis
            }
          }
        ],
        status: 'final',
        type: {
          coding: [
            {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_FORMULAR_ART',
              code: compositionData.formType
            }
          ]
        },
        subject: {
          reference: `${compositionData.patientId}`
        },
        date: compositionData.date,
        author: [
          {
            reference: `${compositionData.practitionerId}`,
            type: 'Practitioner'
          },
          {
            type: 'Device',
            identifier: {
              system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_FOR_Pruefnummer',
              value: 'Y/400/2107/36/999'
            }
          }
        ],
        title: compositionData.title,
        custodian: {
          reference: `${compositionData.organizationId}`
        },
        section: [
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Prescription'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.medicationRequestId}`
              }
            ]
          },
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Coverage'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.coverageId}`
              }
            ]
          }
        ]
      }
    
      return compositionResource
    },

    createMedicationRequestResource(formData) {
      const dbStore = useDbStore();

      const medicationRequestResource = {
        resourceType: 'MedicationRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Prescription|1.1.0']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_StatusCoPayment',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_StatusCoPayment',
              code: '0'
            }
          },
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_EmergencyServicesFee',
            valueBoolean: false
          },
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_BVG',
            valueBoolean: formData.bvg || false
          },
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_Multiple_Prescription',
            extension: [
              {
                url: 'Kennzeichen',
                valueBoolean: formData.mehrfachverordnung || false
              }
            ]
          }
        ],
        status: 'active',
        intent: 'order',
        medicationReference: {
          reference: `${dbStore.getCurrentMedication.id}`
        },
        subject: {
          reference: `${dbStore.getSelectedPatient.easytiUUID}`
        },
        authoredOn: formData.ausstellungsdatum || new Date().toISOString().split('T')[0],
        requester: {
          reference: `${dbStore.getSelectedPractitioner.uuid}`
        },
        insurance: [
          {
            reference: `${dbStore.getSelectedPatient.insuranceInformations.billingInsuranceId}`
          }
        ],
        dispenseRequest: {
          quantity: {
            value: formData.anzahl || 1,
            system: 'http://unitsofmeasure.org',
            code: '{Package}'
          }
        },
        substitution: {
          allowedBoolean: true
        },
        ...(formData.abgabehinweise && {
          note: [
            {
              text: formData.abgabehinweise
            }
          ]
        }),
        ...(formData.dosierung && {
          dosageInstruction: [
            {
              text: formData.dosierung
            }
          ]
        })
      };

      return medicationRequestResource;
    },

    createMedicationResource(medicationData) {
      const medicationResource = {
        resourceType: 'Medication',
        id: crypto.randomUUID(),
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Medication_PZN|1.1.0']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_Medication_Category',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Medication_Category',
              code: medicationData.category
            }
          },
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_Medication_Vaccine',
            valueBoolean: medicationData.isVaccine
          },
          {
            url: 'http://fhir.de/StructureDefinition/normgroesse',
            valueCode: medicationData.normgroesse
          }
        ],
        code: {
          coding: [
            {
              system: 'http://fhir.de/CodeSystem/ifa/pzn',
              code: medicationData.pzn
            }
          ],
          text: medicationData.handelsname
        },
        form: {
          coding: [
            {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_DARREICHUNGSFORM',
              code: medicationData.darreichungsform
            }
          ]
        }
      };
    
      return medicationResource;
    },

    createMedicationFreitextResource(medicationData) {
      const medicationResource = {
        resourceType: 'Medication',
        id: crypto.randomUUID(),
        meta: {
          profile: [
            "https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Medication_FreeText|1.1.0"
          ]
        },
        extension: [
          {
            url: "https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_Medication_Category",
            valueCoding: {
              system: "https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Medication_Category",
              code: "00"
            }
          },
          {
            url: "https://fhir.kbv.de/StructureDefinition/KBV_EX_ERP_Medication_Vaccine",
            valueBoolean: false
          }
        ],
        code: {
          coding: [
            {
              system: "https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Medication_Type",
              code: "freitext"
            }
          ],
          text: medicationData.text
        },
        form: {
          text: medicationData.darreichungsform
        }
      };

      return medicationResource;
    },

    

    createMuster16Bundle() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const basicResourcesStore = useBasicResourcesStore();
      // Erstelle die erforderlichen FHIR-Ressourcen
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      const organizationResource = basicResourcesStore.createOrganizationResource(dbStore.getSelectedOrganization);
      const medicationResource = this.createMedicationResource(dbStore.getCurrentMedication);
      const medicationRequestResource = this.createMedicationRequestResource(eRezeptStore.formData);
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);

      const compositionResource = basicResourcesStore.createCompositionResource({
        id: crypto.randomUUID(),
        patientId: patientResource.id,
        practitionerId: practitionerResource.id,
        organizationId: organizationResource.id,
        medicationRequestId: medicationRequestResource.id,
        coverageId: coverageResource.id,
        formType: 'e16A',
        title: 'elektronische Arzneimittelverordnung',
        date: new Date().toISOString(),
        legalBasis: '00',
      });

      const resources = [
        compositionResource,
        medicationRequestResource,
        medicationResource,
        patientResource,
        practitionerResource,
        organizationResource,
        coverageResource,
      ];

      const bundle = basicResourcesStore.createFHIRBundle(resources);
      return bundle;
    },

    createMuster16FreitextBundle() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const basicResourcesStore = useBasicResourcesStore();
      // Erstelle die erforderlichen FHIR-Ressourcen
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      const organizationResource = basicResourcesStore.createOrganizationResource(dbStore.getSelectedOrganization);
      const medicationResource = this.createMedicationFreitextResource(dbStore.getCurrentMedication);
      const medicationRequestResource = this.createMedicationRequestResource(eRezeptStore.formData);
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);

      const compositionResource = basicResourcesStore.createCompositionResource({
        id: crypto.randomUUID(),
        patientId: patientResource.id,
        practitionerId: practitionerResource.id,
        organizationId: organizationResource.id,
        medicationRequestId: medicationRequestResource.id,
        coverageId: coverageResource.id,
        formType: 'e16A',
        title: 'elektronische Arzneimittelverordnung',
        date: new Date().toISOString(),
        legalBasis: '00',
      });

      const resources = [
        compositionResource,
        medicationRequestResource,
        medicationResource,
        patientResource,
        practitionerResource,
        organizationResource,
        coverageResource,
      ];

      const bundle = basicResourcesStore.createFHIRBundle(resources);
      return bundle;
    },


    createDeviceRequestResource(formData) {
      const dbStore = useDbStore();

      const deviceRequestResource = {
        resourceType: 'DeviceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['https://fhir.carelogic.de/StructureDefinition/CL_PR_ERP_Prescription_Device|0.2.0']
        },
        extension: [
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_StatusCoPayment',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_StatusCoPayment',
              code: '0'
            }
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_EmergencyServicesFee',
            valueBoolean: false
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_BVG',
            valueBoolean: formData.bvg || false
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_DiagnosisICD',
            valueCoding: {
              system: 'http://fhir.de/ValueSet/bfarm/icd-10-gm',
              code: formData.icdCode,
              display: formData.icdText
            }
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_Verordnungszeitraum',
            valuePeriod: {
              start: formData.startDate,
              end: formData.endDate
            }
          }
        ],
        status: 'active',
        intent: 'order',
        codeCodeableConcept: {
          coding: [
            {
              system: 'http://fhir.de/sid/gkv/hmnr',
              code: formData.hmnr,
              display: formData.hmnrText
            }
          ],
          text: formData.hmnrText
        },
        subject: {
          reference: `${dbStore.getSelectedPatient.easytiUUID}`
        },
        authoredOn: formData.ausstellungsdatum || new Date().toISOString().split('T')[0],
        requester: {
          reference: `${dbStore.getSelectedPractitioner.uuid}`
        },
        insurance: [
          {
            reference: `${dbStore.getSelectedPatient.insuranceInformations.billingInsuranceId}`
          }
        ],
        ...(formData.abgabehinweise && {
          note: [
            {
              text: formData.abgabehinweise
            }
          ]
        })
      };

      return deviceRequestResource;
    },


    createMuster16HilfsmittelBundle() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const basicResourcesStore = useBasicResourcesStore();
      // Erstelle die erforderlichen FHIR-Ressourcen
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      const organizationResource = basicResourcesStore.createOrganizationResource(dbStore.getSelectedOrganization);
      const deviceRequestResource = this.createDeviceRequestResource(eRezeptStore.formData);
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);

      const compositionResource = basicResourcesStore.createCompositionHilfsmittelResource({
        id: crypto.randomUUID(),
        patientId: patientResource.id,
        practitionerId: practitionerResource.id,
        organizationId: organizationResource.id,
        deviceRequestId: deviceRequestResource.id,
        coverageId: coverageResource.id,
        musterType: 'e16H',
        date: new Date().toISOString(),
        legalBasis: '00',
      });

      const resources = [
        compositionResource,
        deviceRequestResource,
        patientResource,
        practitionerResource,
        organizationResource,
        coverageResource,
      ];

      const bundle = basicResourcesStore.createHilfsmittelFHIRBundle(resources);
      return bundle;
    }   
  } 
});